import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OrganizationSettings, InvoiceDefaults, BrandingSettings, ProposalDefaults, EmailSettings, FinancialSettings, DefaultCurrencySettings, ProposalConfig, OperationalSettings, TaxSettings, SiteVisitDefaults } from '@/lib/schemas';
import type { ProjectListColorsConfig } from '@/lib/projectListColors';
import { toast } from 'sonner';

// ============================================================================
// Email Notification Preferences types (R2 — email subscriptions)
// ============================================================================

export interface EmailNotificationPreferences {
  email_types: Record<string, boolean>;
  email_address: string | null;
}

// Full notification preferences shape (email_types + in-app enabled_types/thresholds)
interface _FullNotificationPreferences extends EmailNotificationPreferences {
  enabled_types: Record<string, boolean>;
  thresholds: Record<string, number>;
}

// ============================================================================
// User Profile Hooks
// ============================================================================

export function useUpdateProfile() {
  return useMutation({
    mutationFn: (data: { name: string }) => api.profile.update(data),
    onSuccess: () => {
      toast.success('Profile updated successfully');
      // Force page reload to refresh user context with new name
      window.location.reload();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update profile');
    },
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useOrganizationSettings() {
  return useQuery({
    queryKey: ['settings', 'organization'],
    queryFn: () => api.settings.getOrganizationSettings(),
  });
}

export function useInvoiceDefaults() {
  return useQuery({
    queryKey: ['settings', 'invoice-defaults'],
    queryFn: () => api.settings.getInvoiceDefaults(),
  });
}

export function useBrandingSettings() {
  return useQuery({
    queryKey: ['settings', 'branding'],
    queryFn: () => api.settings.getBrandingSettings(),
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<OrganizationSettings>) =>
      api.settings.updateOrganizationSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'organization'] });
      toast.success('Organization settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update organization settings');
    },
  });
}

export function useUpdateInvoiceDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<InvoiceDefaults>) =>
      api.settings.updateInvoiceDefaults(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'invoice-defaults'] });
      toast.success('Invoice defaults updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update invoice defaults');
    },
  });
}

export function useUpdateBrandingSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<BrandingSettings>) =>
      api.settings.updateBrandingSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'branding'] });
      toast.success('Branding settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update branding settings');
    },
  });
}

// ============================================================================
// Proposal Defaults Hooks (Phase 8.3 - Proposal PDF Generation)
// ============================================================================

export function useProposalDefaults() {
  return useQuery({
    queryKey: ['settings', 'proposal-defaults'],
    queryFn: () => api.settings.getProposalDefaults(),
  });
}

export function useUpdateProposalDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ProposalDefaults>) =>
      api.settings.updateProposalDefaults(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'proposal-defaults'] });
      toast.success('Proposal defaults updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update proposal defaults');
    },
  });
}

// ============================================================================
// Email Settings Hooks (Phase 9.1a - Email Integration)
// ============================================================================

export function useEmailSettings() {
  return useQuery({
    queryKey: ['settings', 'email'],
    queryFn: () => api.settings.getEmailSettings(),
  });
}

export function useUpdateEmailSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<EmailSettings>) =>
      api.settings.updateEmailSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'email'] });
      toast.success('Email settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update email settings');
    },
  });
}

// ============================================================================
// Financial Settings Hooks (Phase 9.5b - Revenue Goal Widget)
// ============================================================================

export function useFinancialSettings() {
  return useQuery({
    queryKey: ['settings', 'financial'],
    queryFn: () => api.settings.getFinancialSettings(),
  });
}

export function useUpdateFinancialSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<FinancialSettings>) =>
      api.settings.updateFinancialSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'financial'] });
      toast.success('Financial settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update financial settings');
    },
  });
}

// ============================================================================
// Default Currency Hooks (Phase 10a.9 - Multi-currency invoice support)
// ============================================================================

export function useDefaultCurrency() {
  return useQuery({
    queryKey: ['settings', 'default-currency'],
    queryFn: () => api.settings.getDefaultCurrency(),
  });
}

export function useUpdateDefaultCurrency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DefaultCurrencySettings) =>
      api.settings.updateDefaultCurrency(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'default-currency'] });
      toast.success('Default currency updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update default currency');
    },
  });
}

// ============================================================================
// Proposal Config Hooks (Phase 10c.1 - Proposal terminology customization)
// ============================================================================

export function useProposalConfig() {
  return useQuery({
    queryKey: ['settings', 'proposal-config'],
    queryFn: () => api.settings.getProposalConfig(),
  });
}

export function useUpdateProposalConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ProposalConfig>) =>
      api.settings.updateProposalConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'proposal-config'] });
      toast.success('Proposal settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update proposal settings');
    },
  });
}

// ============================================================================
// Portal Theme Hooks (portal_management feature flag required)
// ============================================================================

// ============================================================================
// Operational Settings Hooks (B4 - Dynamic overdue threshold)
// ============================================================================

export function useOperationalSettings() {
  return useQuery({
    queryKey: ['settings', 'operational'],
    queryFn: () => api.settings.getOperationalSettings(),
    // Cache for 5 minutes — this rarely changes and is read on many project pages
    staleTime: 5 * 60 * 1000,
  });
}

export function usePortalTheme() {
  return useQuery({
    queryKey: ['settings', 'portal-theme'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: () => api.settings.getTheme() as Promise<any>,
  });
}

export function useUpdatePortalTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.settings.updateTheme(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal-theme'] });
      toast.success('Portal theme saved');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save portal theme');
    },
  });
}

// ============================================================================
// Tax Settings Hooks (GST remittance + corp tax dashboard widgets)
// ============================================================================

export function useTaxSettings() {
  return useQuery({
    queryKey: ['settings', 'tax'],
    queryFn: () => api.settings.getTaxSettings(),
  });
}

export function useUpdateTaxSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<TaxSettings>) =>
      api.settings.updateTaxSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'tax'] });
      toast.success('Tax settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update tax settings');
    },
  });
}

// ============================================================================
// Tax Engine Status Hook (v0.17 taxclass-label fix, SPEC-bc-pst-tax-engine-2026-08-04)
// ============================================================================

/**
 * Fetch the tenant's tax engine mode ('legacy'/'rules') + rule enablement.
 *
 * Read-only, no mutation counterpart. Drives whether the tax-class labels
 * (lib/taxClasses.ts) may say a tax is actually charged. Cached for 5 minutes
 * — this is tenant configuration that changes at most once (the October
 * flip), not per-invoice state, and it is read on every invoice line.
 */
export function useTaxEngineStatus() {
  return useQuery({
    queryKey: ['settings', 'tax-engine'],
    queryFn: () => api.settings.getTaxEngineStatus(),
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Project List Colours Hooks (SPEC-project-list-colour-rules / R2)
// ============================================================================

/**
 * Fetch the tenant's project-list colouring rules config (R1 GET endpoint).
 *
 * Returns the inert default ({version:1, rules:[]}) when the tenant has no doc,
 * so the project list renders unchanged. Cached for 5 minutes since rules
 * rarely change and this is read on the project list page. The project list
 * renders normally while this query is loading — callers should treat
 * `data?.rules ?? []` as the rule set (empty ⇒ no-op).
 */
export function useProjectListColors() {
  return useQuery<ProjectListColorsConfig>({
    queryKey: ['settings', 'project-list-colors'],
    queryFn: () => api.settings.getProjectListColors(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Save the tenant's project-list colouring rules (R1 PUT endpoint).
 *
 * PUT is a FULL REPLACE — the caller must always pass the COMPLETE
 * `ProjectListColorsConfig` (every rule), not a delta. On success the
 * `['settings', 'project-list-colors']` query is invalidated so the project
 * list recolours without a reload.
 */
export function useUpdateProjectListColors() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProjectListColorsConfig) =>
      api.settings.updateProjectListColors(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'project-list-colors'] });
      toast.success('Project list colours saved');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save project list colours');
    },
  });
}

// ============================================================================
// Site Visit Defaults Hooks
// ============================================================================

export function useSiteVisitDefaults() {
  return useQuery({
    queryKey: ['settings', 'site-visit-defaults'],
    queryFn: () => api.settings.getSiteVisitDefaults(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateSiteVisitDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<SiteVisitDefaults>) =>
      api.settings.updateSiteVisitDefaults(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'site-visit-defaults'] });
      toast.success('Site visit defaults updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update site visit defaults');
    },
  });
}

// ============================================================================
// Email Notification Preferences Hooks (R2 — email subscriptions)
// ============================================================================

/**
 * Fetch the user's email notification preferences.
 * Shares the ['notification-preferences'] query key with NotificationPreferencesTab
 * so both components read from the same cache.
 */
export function useEmailNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.get('/api/notifications/preferences') as Promise<_FullNotificationPreferences>,
  });
}

/**
 * Save email notification preferences (email_types + email_address).
 * Merges with the cached in-app notification prefs so enabled_types/thresholds
 * are not overwritten.
 */
export function useUpdateEmailNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EmailNotificationPreferences) => {
      // Preserve existing in-app notification prefs from the cache
      const cached = queryClient.getQueryData<_FullNotificationPreferences>(['notification-preferences']);
      return api.put('/api/notifications/preferences', {
        enabled_types: cached?.enabled_types ?? {},
        thresholds: cached?.thresholds ?? {},
        email_types: data.email_types,
        email_address: data.email_address ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Email notification preferences saved');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save email notification preferences');
    },
  });
}
